import React, { useState } from 'react';
import GlobalProps from '../../../../components/globalProps';
import { Product, persistImageChanges, patchProduct } from '../../../../model/product';
import { Label } from '../../../../components/Form/Label/Label';
import { LogoUploader, TempImage } from '../../components/LogoUploader';
import { Image } from '../../../../model/asset';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { FormTextArea } from '../../../../components/Form/TextArea/FormTextArea';
import { TicketLine } from '../../../../model/event';

interface Props extends GlobalProps {
  model: Product;
  locale: 'En' | 'De';
  onClose: () => void;
}

export function ContentForm({ locale, model: propsModel, onClose }: Props): JSX.Element {
  const [images, setImages] = useState<Array<Image | TempImage>>(propsModel.images || []);
  const [overrideCanSubmit, setOverrideCanSubmit] = useState(false);

  const { fields, model, changes, onSubmit, valid, dirty, submissionStatus } = useForm({
    model: {
      ...propsModel,
      additionalInformation: { ...(propsModel.additionalInformation as TicketLine) },
      details: { ...(propsModel.details as TicketLine) },
    },
    onSubmit: async () => {
      try {
        await persistImageChanges(model.id, images);
        await patchProduct(model.id, changes);
        onClose();
      } catch (error) {
        handleError(error as Error);
      }
    },
  });

  function updateImages(images: Array<Image | TempImage>): void {
    const updatedOrder = images.map((image, index) => ({
      ...image,
      order: index,
    }));
    setImages(updatedOrder);
  }

  return (
    <div className="content-card">
      <FormForm
        onSubmit={onSubmit}
        canSubmit={overrideCanSubmit || (valid && dirty)}
        loading={submissionStatus === 'submitting'}
        onClose={onClose}
      >
        <Stack>
          <Label title={t.general.generalInformation} />
          <FormTextField label={t.model.tourArtists._name} {...fields[`artist${locale}`]} />
          <FormTextField label={t.general.type} {...fields[`productType${locale}`]} />
          <FormTextField label={t.general.motive} {...fields[`name${locale}`]} />
          <FormTextField label={t.general.color} {...fields[`color${locale}`]} />
          <FormTextArea label={t.general.description} {...fields[`description${locale}`]} />
          <FormTextField label={t.general.material} {...fields[`material${locale}`]} />
          <FormTextArea
            label={t.general.productSafetyDescription}
            {...fields[`productSafetyDescription${locale}`]}
            required={true}
          />
          <Spacer size="none" />
          <Label title={t.model.product.additionalInfo} />
          <FormTextField
            label={`${t.model.product.additionalInfoSide} ${t.general.headline}`}
            {...fields.additionalInformation.fields[`title${locale}`]}
          />
          <FormTextArea
            label={`${t.model.product.additionalInfoSide} ${t.general.text}`}
            {...fields.additionalInformation.fields[`description${locale}`]}
          />
          <FormTextField
            label={`${t.model.product.additionalInfoBellow} ${t.general.headline}`}
            {...fields.details.fields[`title${locale}`]}
          />
          <FormTextArea
            label={`${t.model.product.additionalInfoBellow} ${t.general.text}`}
            {...fields.details.fields[`description${locale}`]}
          />
          <Spacer size="none" />
          <Label title={`${t.model.product._name} ${t.model.image._plural}`} />
          <div className="content--card__product-text">{t.model.product.productImageDescription}</div>
          <LogoUploader
            currentImages={images}
            onChange={(newImages) => {
              updateImages(newImages);
              setOverrideCanSubmit(true);
            }}
          />
        </Stack>
      </FormForm>
    </div>
  );
}
